import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Button,
  TextField,
  CardContent,
  Breadcrumbs
} from '@material-ui/core';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import { useGet, usePost, usePut } from '../../API/request';
import React, { useEffect, useState } from 'react';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import Alert from '@material-ui/core/Alert';

const SeoEdit = () => {
  const navigate = useNavigate();
  const getU = useGet();
  const putU = usePut();

  const [submitDisabled, setSubmitDisabled] = useState(false);
  const { id } = useParams();

  const [values, setValues] = useState({
    name: '',
    param: '',
    img: '',
    description: '',
    schema: ''
  });

  const [errors, setErrors] = useState({
    name: false,
    param: false,
    img: false,
    description: false,
    schema: false
  });

  const [alert, setAlert] = useState({
    txt: '',
    isVisible: false,
    type: 'error'
  });

  const [variables, setVariables] = useState(null);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setValues((prevValues) => ({
      ...prevValues,
      [name]: value
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: false
    }));
  };

  const showAlert = (type, text) => {
    setAlert({
      txt: text,
      type,
      isVisible: true
    });

    setTimeout(() => {
      setAlert({
        txt: text,
        type,
        isVisible: false
      });

      setSubmitDisabled(false);
    }, 1400);
  };

  const getAllAvailableParams = () => {
    if (!variables) return [];

    const allParams = [];

    Object.values(variables).forEach((section) => {
      Object.keys(section).forEach((param) => {
        allParams.push(param);
      });
    });

    return allParams;
  };

  const validate = () => {
    let validComplete = true;
    let formErrors = { ...errors };

    const requiredFields = [
      { field: 'name', errorMessage: 'Введите имя' },
      { field: 'param', errorMessage: 'Введите параметр' },
      { field: 'img', errorMessage: 'Добавьте изображение' },
      { field: 'description', errorMessage: 'Добавьте описание' },
      { field: 'schema', errorMessage: 'Добавьте схему' }
    ];

    requiredFields.forEach(({ field, errorMessage }) => {
      if (values[field] === '') {
        validComplete = false;
        formErrors[field] = true;
        showAlert('error', errorMessage);
      }
    });

    const fieldsToValidate = ['name', 'description'];
    const availableParams = getAllAvailableParams();

    fieldsToValidate.forEach((field) => {
      const matches = values[field].match(/{{(.*?)}}/g);
      if (matches) {
        matches.forEach((match) => {
          if (!availableParams.includes(match)) {
            validComplete = false;
            formErrors[field] = true;
            showAlert(
              'error',
              `Недопустимый параметр "${match}" в поле "${field}". Используйте только доступные параметры из списка.`
            );
          }
        });
      }
    });

    setErrors(formErrors);
    return validComplete;
  };

  const loadSeo = () => {
    getU(`seo/specific/${id}`)
      .then((resp) => {
        if (resp.status === 'success') {
          setValues(resp.data.seo);
        }
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {});
  };

  const loadVariables = () => {
    getU(`seo/variables`)
      .then((resp) => {
        if (resp.status === 'success') {
          setVariables(resp.data);
        }
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {});
  };

  const submit = async () => {
    if (validate()) {
      setSubmitDisabled(true);

      const Data = new FormData();
      Data.append('name', values.name);
      Data.append('param', values.param);
      Data.append('schema', values.schema);
      Data.append('img', values.img);
      Data.append('description', values.description);

      putU(`seo/${id}`, Data)
        .then((resp) => {
          if (resp.status === 'success') {
            showAlert('success', 'SEO обновлён');
          } else {
            showAlert('error', 'Ошибка');
          }
        })
        .catch((err) => {
          showAlert(
            'error',
            `Ошибка сервера: ${err.response?.data?.message || err.message}`
          );
          setSubmitDisabled(false);
        });
    }
  };

  const formatVariables = () => {
    if (!variables) return '';

    let result = '';

    Object.entries(variables).forEach(([section, params]) => {
      result += `${section.toUpperCase()}:\n`;
      Object.entries(params).forEach(([param, description]) => {
        result += `  ${param} - ${description}\n`;
      });
      result += '\n';
    });

    return result;
  };

  useEffect(() => {
    loadSeo();
    loadVariables();
  }, []);

  return (
    <>
      <Helmet>
        <title>Редактировать SEO</title>
      </Helmet>
      <Box sx={{ pt: 2 }}>
        <Container maxWidth={false}>
          <Button startIcon={<ChevronLeft />} onClick={() => navigate(-1)}>
            Назад
          </Button>
          <Breadcrumbs aria-label="breadcrumb" style={{ fontSize: 14 }}>
            <RouterLink underline="hover" color="inherit" to="/app/seo">
              SEO
            </RouterLink>
            <p>Редактировать SEO</p>
          </Breadcrumbs>
        </Container>
      </Box>
      <Box sx={{ backgroundColor: 'background.default', minHeight: '100%' }}>
        <Container maxWidth={false}>
          <Box sx={{ pt: 2 }}>
            <form>
              <Card>
                <CardHeader title="Редактирование SEO" />
                <Divider />
                <CardContent sx={{ position: 'relative' }}>
                  <Box
                    display={'flex'}
                    alignItems={'center'}
                    flexDirection={'column'}
                  >
                    <TextField
                      sx={{ mr: 3, width: '70%' }}
                      label="Имя"
                      fullWidth
                      margin="normal"
                      name="name"
                      onChange={handleChange}
                      type="text"
                      value={values.name}
                      variant="outlined"
                      error={errors.name}
                      helperText="Можно использовать только допустимые параметры из списка"
                    />
                    <TextField
                      sx={{ mr: 3, width: '70%' }}
                      label="Параметр"
                      fullWidth
                      margin="normal"
                      name="param"
                      onChange={handleChange}
                      type="text"
                      value={values.param}
                      variant="outlined"
                      error={errors.param}
                      readOnly
                      disabled
                    />
                    <TextField
                      sx={{ mr: 3, width: '70%' }}
                      fullWidth
                      label="Описание"
                      margin="normal"
                      name="description"
                      onChange={handleChange}
                      type="text"
                      value={values.description}
                      variant="outlined"
                      multiline
                      rows={4}
                      helperText="Можно использовать только допустимые параметры ис списка"
                    />
                    <TextField
                      sx={{ mr: 3, width: '70%' }}
                      label="Схема"
                      fullWidth
                      margin="normal"
                      name="schema"
                      onChange={handleChange}
                      type="text"
                      value={values.schema}
                      variant="outlined"
                      error={errors.schema}
                      multiline
                      rows={6}
                    />
                    <TextField
                      sx={{ mr: 3, width: '70%' }}
                      label="Изображение"
                      fullWidth
                      margin="normal"
                      name="img"
                      onChange={handleChange}
                      type="text"
                      value={values.img}
                      variant="outlined"
                      error={errors.img}
                    />
                    <TextField
                      sx={{ mr: 3, width: '70%' }}
                      label="Список доступных параметров"
                      disabled
                      fullWidth
                      margin="normal"
                      name="availableParams"
                      type="text"
                      value={formatVariables()}
                      multiline
                      rows={10}
                      helperText="Эти параметры можно использовать в полях 'Имя' и 'Описание'"
                    />
                  </Box>
                  <Alert
                    severity={alert.type}
                    style={{ display: alert.isVisible ? 'flex' : 'none' }}
                  >
                    {alert.txt}
                  </Alert>
                </CardContent>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={submit}
                    disabled={submitDisabled}
                  >
                    Редактировать
                  </Button>
                </Box>
              </Card>
            </form>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default SeoEdit;
